import { useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  useAdminChildProgress,
  useAdminChildDetail,
  type DetailedProgress,
} from "../../hooks/useAdminUsers";

/**
 * ChildProgressPage - Admin page to view detailed challenge-by-challenge progress.
 *
 * Displays:
 * - Discipline tabs for navigation
 * - Expandable level accordions
 * - Challenge completion details with stars, attempts, timestamps
 * - Summary statistics panel
 */
export function ChildProgressPage() {
  const { childId } = useParams<{ childId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get the selected discipline from URL, default to "math"
  const selectedDiscipline = searchParams.get("discipline") || "math";

  // Fetch child basic info for the header
  const { child: childDetail } = useAdminChildDetail(childId);

  // Fetch detailed progress for selected discipline
  const { progress, isLoading, error } = useAdminChildProgress(
    childId,
    selectedDiscipline
  );

  // Track which levels are expanded
  const [expandedLevels, setExpandedLevels] = useState<Set<number>>(
    new Set([1, 2, 3]) // Start with first 3 levels expanded
  );

  const toggleLevel = (levelNumber: number) => {
    setExpandedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(levelNumber)) {
        next.delete(levelNumber);
      } else {
        next.add(levelNumber);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (progress) {
      setExpandedLevels(new Set(progress.levels.map((l) => l.number)));
    }
  };

  const collapseAll = () => {
    setExpandedLevels(new Set());
  };

  const handleDisciplineChange = (slug: string) => {
    setSearchParams({ discipline: slug });
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error || !progress) {
    return (
      <div className="space-y-6">
        <BackLink childId={childId} childName={childDetail?.child.name} />
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center gap-3">
            <ErrorIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-red-800">
                Error Loading Progress
              </h2>
              <p className="text-sm text-red-600 mt-1">
                {error || "Progress data not found"}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <BackLink childId={childId} childName={progress.child.name} />

      {/* Page header */}
      <PageHeader
        childName={progress.child.name}
        discipline={progress.discipline}
      />

      {/* Discipline tabs */}
      <DisciplineTabs
        selected={selectedDiscipline}
        onChange={handleDisciplineChange}
      />

      {/* Summary statistics */}
      <SummaryStats summary={progress.summary} />

      {/* Expand/Collapse controls */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Level Progress</h2>
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="text-sm text-magic-600 hover:text-magic-700 font-medium"
          >
            Expand All
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={collapseAll}
            className="text-sm text-magic-600 hover:text-magic-700 font-medium"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Level accordions */}
      <div className="space-y-3">
        {progress.levels.map((level) => (
          <LevelAccordion
            key={level.number}
            level={level}
            isExpanded={expandedLevels.has(level.number)}
            onToggle={() => toggleLevel(level.number)}
          />
        ))}
      </div>
    </div>
  );
}

// ----- Sub-Components -----

function BackLink({
  childId,
  childName,
}: {
  childId?: string;
  childName?: string;
}) {
  if (childId) {
    return (
      <Link
        to={`/admin/children/${childId}`}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to {childName ? `${childName}'s Profile` : "Child Profile"}
      </Link>
    );
  }

  return (
    <Link
      to="/admin/users"
      className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
    >
      <ArrowLeftIcon className="w-4 h-4" />
      Back to Users
    </Link>
  );
}

function PageHeader({
  childName,
  discipline,
}: {
  childName: string;
  discipline: DetailedProgress["discipline"];
}) {
  const config = DISCIPLINE_CONFIG[discipline.slug] ?? DISCIPLINE_CONFIG.math!;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-4">
        <div
          className={`w-14 h-14 rounded-xl ${config.bgColor} flex items-center justify-center flex-shrink-0`}
        >
          <span className="text-3xl">{config.emoji}</span>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {childName}'s Progress - {discipline.name}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Guided by {discipline.character}
          </p>
        </div>
      </div>
    </div>
  );
}

function DisciplineTabs({
  selected,
  onChange,
}: {
  selected: string;
  onChange: (slug: string) => void;
}) {
  const disciplines = [
    { slug: "math", name: "Math" },
    { slug: "spelling", name: "Spelling" },
    { slug: "patterns", name: "Patterns" },
    { slug: "typing", name: "Typing" },
    { slug: "programming", name: "Coding" },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
      <div className="flex gap-1">
        {disciplines.map((d) => {
          const config = DISCIPLINE_CONFIG[d.slug] ?? DISCIPLINE_CONFIG.math!;
          const isSelected = selected === d.slug;

          return (
            <button
              key={d.slug}
              onClick={() => onChange(d.slug)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                isSelected
                  ? `${config.selectedBg} ${config.selectedText}`
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <span>{config.emoji}</span>
              <span className="hidden sm:inline">{d.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SummaryStats({
  summary,
}: {
  summary: DetailedProgress["summary"];
}) {
  const completionPercent =
    summary.totalChallenges > 0
      ? Math.round((summary.completedChallenges / summary.totalChallenges) * 100)
      : 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          icon={<CheckCircleIcon className="w-5 h-5" />}
          iconBg="bg-meadow-100"
          iconColor="text-meadow-600"
          value={`${summary.completedChallenges}/${summary.totalChallenges}`}
          label="Completed"
          sublabel={`${completionPercent}%`}
        />
        <StatCard
          icon={<StarIcon className="w-5 h-5" filled />}
          iconBg="bg-sunshine-100"
          iconColor="text-sunshine-600"
          value={`${summary.totalStars}/${summary.maxStars}`}
          label="Stars Earned"
        />
        <StatCard
          icon={<ChartIcon className="w-5 h-5" />}
          iconBg="bg-sky-100"
          iconColor="text-sky-600"
          value={summary.avgStarsPerChallenge.toFixed(1)}
          label="Avg Stars"
        />
        <StatCard
          icon={<RetryIcon className="w-5 h-5" />}
          iconBg="bg-magic-100"
          iconColor="text-magic-600"
          value={summary.avgAttemptsPerChallenge.toFixed(1)}
          label="Avg Attempts"
        />
      </div>
    </div>
  );
}

function StatCard({
  icon,
  iconBg,
  iconColor,
  value,
  label,
  sublabel,
}: {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  value: string | number;
  label: string;
  sublabel?: string;
}) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
      <div
        className={`w-10 h-10 rounded-lg ${iconBg} ${iconColor} flex items-center justify-center flex-shrink-0`}
      >
        {icon}
      </div>
      <div>
        <p className="text-xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500">
          {label}
          {sublabel && (
            <span className="text-meadow-600 font-medium ml-1">{sublabel}</span>
          )}
        </p>
      </div>
    </div>
  );
}

function LevelAccordion({
  level,
  isExpanded,
  onToggle,
}: {
  level: DetailedProgress["levels"][0];
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const statusConfig = {
    complete: {
      icon: <CheckIcon className="w-4 h-4" />,
      text: "Complete",
      bg: "bg-meadow-100",
      textColor: "text-meadow-700",
    },
    in_progress: {
      icon: <PlayIcon className="w-4 h-4" />,
      text: "In Progress",
      bg: "bg-sky-100",
      textColor: "text-sky-700",
    },
    locked: {
      icon: <LockIcon className="w-4 h-4" />,
      text: "Locked",
      bg: "bg-gray-100",
      textColor: "text-gray-500",
    },
  };

  const status = statusConfig[level.status];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Accordion Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div
            className={`w-10 h-10 rounded-lg ${status.bg} ${status.textColor} flex items-center justify-center`}
          >
            {status.icon}
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">
              Level {level.number}: {level.name}
            </h3>
            <p className="text-sm text-gray-500">
              {level.completedChallenges}/{level.totalChallenges} challenges
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <span
            className={`text-xs font-medium px-2.5 py-1 rounded-full ${status.bg} ${status.textColor}`}
          >
            {status.text}
          </span>
          <ChevronIcon
            className={`w-5 h-5 text-gray-400 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {level.challenges.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              No challenges in this level
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {level.challenges.map((challenge) => (
                <ChallengeRow key={challenge.id} challenge={challenge} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChallengeRow({
  challenge,
}: {
  challenge: DetailedProgress["levels"][0]["challenges"][0];
}) {
  const statusConfig = {
    completed: {
      icon: <CheckCircleIcon className="w-5 h-5 text-meadow-500" />,
      bg: "bg-white",
    },
    not_started: {
      icon: <CircleIcon className="w-5 h-5 text-gray-300" />,
      bg: "bg-white",
    },
    locked: {
      icon: <LockIcon className="w-5 h-5 text-gray-300" />,
      bg: "bg-gray-50",
    },
  };

  const status = statusConfig[challenge.status];

  return (
    <div
      className={`flex items-center justify-between px-4 py-3 ${status.bg} ${
        challenge.status === "locked" ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        {status.icon}
        <div>
          <p
            className={`font-medium ${
              challenge.status === "locked" ? "text-gray-400" : "text-gray-900"
            }`}
          >
            {challenge.order}. {challenge.name}
          </p>
          {challenge.status === "completed" && challenge.completedAt && (
            <p className="text-xs text-gray-400">
              {formatDateTime(challenge.completedAt)}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Stars display */}
        {challenge.status === "completed" && challenge.stars !== null && (
          <div className="flex items-center gap-0.5">
            {[1, 2, 3].map((starNum) => (
              <StarIcon
                key={starNum}
                className={`w-4 h-4 ${
                  starNum <= challenge.stars!
                    ? "text-sunshine-500"
                    : "text-gray-200"
                }`}
                filled={starNum <= challenge.stars!}
              />
            ))}
          </div>
        )}

        {/* Attempts */}
        {challenge.status === "completed" && challenge.attempts > 0 && (
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {challenge.attempts} attempt{challenge.attempts !== 1 ? "s" : ""}
          </span>
        )}

        {/* Locked badge */}
        {challenge.status === "locked" && (
          <span className="text-xs text-gray-400">Locked</span>
        )}

        {/* Not started badge */}
        {challenge.status === "not_started" && (
          <span className="text-xs text-gray-400">Not started</span>
        )}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-40" />

      {/* Header skeleton */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gray-200" />
          <div className="flex-1">
            <div className="h-8 bg-gray-200 rounded w-64 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-40" />
          </div>
        </div>
      </div>

      {/* Tabs skeleton */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
        <div className="flex gap-1">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex-1 h-10 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>

      {/* Summary skeleton */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="h-6 bg-gray-200 rounded w-32 mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>

      {/* Levels skeleton */}
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-gray-200" />
              <div className="flex-1">
                <div className="h-5 bg-gray-200 rounded w-48 mb-2" />
                <div className="h-4 bg-gray-100 rounded w-32" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----- Configuration -----

const DISCIPLINE_CONFIG: Record<
  string,
  {
    emoji: string;
    bgColor: string;
    selectedBg: string;
    selectedText: string;
  }
> = {
  math: {
    emoji: "🔢",
    bgColor: "bg-luna-light",
    selectedBg: "bg-luna-100",
    selectedText: "text-luna-700",
  },
  spelling: {
    emoji: "🔤",
    bgColor: "bg-petal-light",
    selectedBg: "bg-petal-100",
    selectedText: "text-petal-700",
  },
  patterns: {
    emoji: "🎨",
    bgColor: "bg-gradient-to-br from-prism-red to-prism-purple",
    selectedBg: "bg-gradient-to-r from-prism-red/20 to-prism-purple/20",
    selectedText: "text-prism-purple",
  },
  typing: {
    emoji: "⌨️",
    bgColor: "bg-whiskers-light",
    selectedBg: "bg-whiskers-100",
    selectedText: "text-whiskers-700",
  },
  programming: {
    emoji: "🤖",
    bgColor: "bg-pixel-light",
    selectedBg: "bg-pixel-100",
    selectedText: "text-pixel-700",
  },
};

// ----- Utility Functions -----

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (isToday) {
    return `Today, ${time}`;
  }

  if (isYesterday) {
    return `Yesterday, ${time}`;
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// ----- Icons -----

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 19l-7-7m0 0l7-7m-7 7h18"
      />
    </svg>
  );
}

function StarIcon({
  className,
  filled,
}: {
  className?: string;
  filled?: boolean;
}) {
  return (
    <svg
      className={className}
      fill={filled ? "currentColor" : "none"}
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={filled ? 0 : 2}
        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
      />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <circle cx="12" cy="12" r="9" strokeWidth={2} />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}

function RetryIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

export default ChildProgressPage;
